import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../api/axios';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  CircularProgress,
  FormHelperText,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import { toast } from 'react-toastify';

const AppointmentForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    service: '',
    date: new Date(),
    timeSlot: '',
    notes: ''
  });
  
  const [categories, setCategories] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [services, setServices] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch categories when component mounts
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        // Fetch all unique business categories from the backend
        const response = await api.get('/api/users/business-categories/all');
        setCategories(response.data || []);
      } catch (error) {
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  // Fetch businesses when category is selected
  useEffect(() => {
    const fetchBusinessesByCategory = async () => {
      if (!selectedCategory) {
        setBusinesses([]);
        return;
      }
      
      setLoadingBusinesses(true);
      
      try {
        const response = await api.get(`/api/users/businesses/category/${selectedCategory}`);
        setBusinesses(response.data || []);
      } catch (error) {
        console.error(`Error fetching businesses for category ${selectedCategory}:`, error);
        setBusinesses([]);
      } finally {
        setLoadingBusinesses(false);
      }
    };

    fetchBusinessesByCategory();
    // Reset subsequent selections
    setSelectedBusiness('');
    setFormData(prev => ({ ...prev, service: '', timeSlot: '' }));
    setServices([]);
    setTimeSlots([]);
  }, [selectedCategory]);

  // Fetch services when business is selected
  useEffect(() => {
    const fetchServices = async () => {
      if (!selectedBusiness) {
        setServices([]);
        return;
      }
      
      setLoadingServices(true);
      
      try {
        const response = await api.get(`/api/services/business/${selectedBusiness}`);
        setServices(response.data || []);
      } catch (error) {
        console.error('Error fetching services:', error);
        setServices([]);
      } finally {
        setLoadingServices(false);
      }
    };

    if (selectedBusiness) {
    fetchServices();
      // Reset service selection
      setFormData(prev => ({ ...prev, service: '', timeSlot: '' }));
      setTimeSlots([]);
    }
  }, [selectedBusiness]);

  // Fetch available time slots when service and date are selected
  useEffect(() => {
    const fetchTimeSlots = async () => {
      if (!selectedBusiness || !formData.service || !formData.date) {
        setTimeSlots([]);
        return;
      }
      
      setLoadingTimeSlots(true);
      
      try {
        const serviceObj = services.find(s => s._id === formData.service);
        if (!serviceObj) {
          console.error('Selected service not found');
          setTimeSlots([]);
          setLoadingTimeSlots(false);
          return;
        }

        const requestDate = formData.date.toISOString().split('T')[0];
        const serviceDuration = serviceObj.duration || 30;
        
        console.log(`Fetching time slots for business: ${selectedBusiness}, date: ${requestDate}, duration: ${serviceDuration}`);
        
        try {
          const response = await api.get(
            `/api/availability/timeslots/${selectedBusiness}/${requestDate}/${serviceDuration}`
          );
          
          // Ensure we always have an array
          const slots = Array.isArray(response.data) ? response.data : [];
          console.log(`Received ${slots.length} time slots`);
          
          setTimeSlots(slots);
        } catch (apiError) {
          console.error('API error fetching time slots:', apiError);
          
          // Fallback to the old endpoint if the new one fails
          try {
            const fallbackResponse = await api.get(
              `/api/availability/slots/${selectedBusiness}/${requestDate}`
            );
            
            const fallbackSlots = Array.isArray(fallbackResponse.data) ? fallbackResponse.data : [];
            console.log(`Fallback: Received ${fallbackSlots.length} time slots`);
            
            setTimeSlots(fallbackSlots);
          } catch (fallbackError) {
            console.error('Fallback API error:', fallbackError);
            setTimeSlots([]);
          }
        }
      } catch (error) {
        console.error('Error in fetchTimeSlots:', error);
        setTimeSlots([]);
      } finally {
        setLoadingTimeSlots(false);
      }
    };

    if (formData.service) {
    fetchTimeSlots();
      // Reset time slot selection
      setFormData(prev => ({ ...prev, timeSlot: '' }));
    }
  }, [selectedBusiness, formData.service, formData.date, services]);

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  const handleBusinessChange = (e) => {
    setSelectedBusiness(e.target.value);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      date
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedCategory) newErrors.category = 'Please select a category';
    if (!selectedBusiness) newErrors.business = 'Please select a business';
    if (!formData.service) newErrors.service = 'Please select a service';
    if (!formData.timeSlot) newErrors.timeSlot = 'Please select a time slot';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const selectedSlot = timeSlots.find(slot => slot.startTime === formData.timeSlot);
      
      if (!selectedSlot) {
        throw new Error('Selected time slot not found');
      }
      
      const appointmentData = {
        service: formData.service,
        date: formData.date.toISOString(),
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        notes: formData.notes
      };
      
      await api.post('/api/appointments', appointmentData);
      toast.success('Appointment booked successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error booking appointment:', error);
      toast.error('Failed to book appointment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Book an Appointment
      </Typography>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Step 1: Select Category */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.category}>
                <InputLabel id="category-select-label">Select Category</InputLabel>
                <Select
                  labelId="category-select-label"
                  id="category"
                  value={selectedCategory}
                  label="Select Category"
                  onChange={handleCategoryChange}
                  disabled={loadingCategories}
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
                {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
              </FormControl>
            </Grid>
            
            {/* Step 2: Select Business */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.business}>
                <InputLabel id="business-select-label">Select Business</InputLabel>
                <Select
                  labelId="business-select-label"
                  id="business"
                  value={selectedBusiness}
                  label="Select Business"
                  onChange={handleBusinessChange}
                  disabled={loadingBusinesses || !selectedCategory}
                >
                  {businesses.map((business) => (
                    <MenuItem key={business._id} value={business._id}>
                      {business.businessDetails?.businessName || business.name || 'Business'}
                    </MenuItem>
                  ))}
                </Select>
                {errors.business && <FormHelperText>{errors.business}</FormHelperText>}
                {selectedCategory && businesses.length === 0 && !loadingBusinesses && (
                  <FormHelperText>No businesses found in this category</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.service}>
                <InputLabel id="service-select-label">Select Service</InputLabel>
                <Select
                  labelId="service-select-label"
                  id="service"
                  name="service"
                  value={formData.service}
                  label="Select Service"
                  onChange={handleChange}
                  disabled={loadingServices || !selectedBusiness}
                >
                  {services.map((service) => (
                    <MenuItem key={service._id} value={service._id}>
                      {service.name} - ${service.price} ({service.duration} mins)
                    </MenuItem>
                  ))}
                </Select>
                {errors.service && <FormHelperText>{errors.service}</FormHelperText>}
                {selectedBusiness && services.length === 0 && !loadingServices && (
                  <FormHelperText>No services found for this business</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  Select Date:
                </Typography>
                <DatePicker
                  selected={formData.date}
                  onChange={handleDateChange}
                  minDate={new Date()}
                  dateFormat="MMMM d, yyyy"
                  className="form-control"
                  customInput={
                    <TextField 
                      fullWidth
                    error={!!errors.date}
                    helperText={errors.date}
                    InputProps={{
                      style: { height: '56px' }  // Match height with other form controls
                    }}
                    />
                  }
                />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.timeSlot}>
                <InputLabel id="time-slot-label">Select Time Slot</InputLabel>
                <Select
                  labelId="time-slot-label"
                  id="timeSlot"
                  name="timeSlot"
                  value={formData.timeSlot}
                  label="Select Time Slot"
                  onChange={handleChange}
                  disabled={loadingTimeSlots || !formData.service}
                >
                  {Array.isArray(timeSlots) ? timeSlots.map((slot, index) => (
                    <MenuItem key={index} value={slot.startTime}>
                      {slot.startTime} - {slot.endTime}
                    </MenuItem>
                  )) : null}
                </Select>
                {errors.timeSlot && <FormHelperText>{errors.timeSlot}</FormHelperText>}
                {formData.service && Array.isArray(timeSlots) && timeSlots.length === 0 && !loadingTimeSlots && (
                  <FormHelperText>No available time slots for this date. Please select another date.</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                id="notes"
                name="notes"
                label="Additional Notes (Optional)"
                value={formData.notes}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Book Appointment'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
      
      {formData.service && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Appointment Summary
            </Typography>
            <Typography variant="body2">
              <strong>Category:</strong> {selectedCategory}
            </Typography>
            <Typography variant="body2">
              <strong>Business:</strong> {businesses.find(b => b._id === selectedBusiness)?.businessDetails?.businessName || businesses.find(b => b._id === selectedBusiness)?.name}
            </Typography>
            <Typography variant="body2">
              <strong>Service:</strong> {services.find(s => s._id === formData.service)?.name}
            </Typography>
            <Typography variant="body2">
              <strong>Price:</strong> ${services.find(s => s._id === formData.service)?.price}
            </Typography>
            <Typography variant="body2">
              <strong>Duration:</strong> {services.find(s => s._id === formData.service)?.duration} minutes
            </Typography>
            {formData.timeSlot && (
              <>
                <Typography variant="body2">
                  <strong>Date:</strong> {formData.date.toLocaleDateString()}
                </Typography>
                <Typography variant="body2">
                  <strong>Time:</strong> {formData.timeSlot} - {Array.isArray(timeSlots) && timeSlots.find(slot => slot.startTime === formData.timeSlot)?.endTime}
                </Typography>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AppointmentForm; 